(() => {
  if (window.__orlueurSocialUGCLoaded) return;
  window.__orlueurSocialUGCLoaded = true;

  const initialized = new WeakSet();
  const warmedOrigins = new Set();

  const addConnectionHint = (origin) => {
    if (!origin || warmedOrigins.has(origin)) return;
    warmedOrigins.add(origin);

    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = origin;
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);

    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = origin;
    document.head.appendChild(dnsPrefetch);
  };

  class OrlueurSocialUGC {
    constructor(section) {
      this.section = section;
      this.rail = section.querySelector('[data-osu-rail]');
      this.cards = this.rail ? Array.from(this.rail.querySelectorAll('[data-osu-card]')) : [];
      this.previousButton = section.querySelector('[data-osu-previous]');
      this.nextButton = section.querySelector('[data-osu-next]');
      this.interval = Number(section.dataset.interval) || 15000;
      this.autoplay = section.dataset.autoplay === 'true';
      this.advanceOnEnd = section.dataset.advanceOnEnd === 'true';
      this.index = 0;
      this.timer = null;
      this.isVisible = false;
      this.isInteracting = false;
      this.scrollTimer = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onPlayerMessage = this.handlePlayerMessage.bind(this);

      if (!this.rail || !this.cards.length) return;

      this.bindEvents();
      this.setActive(0, false);
      this.observeSection();
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => this.goTo(this.index - 1, true));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1, true));

      this.rail.addEventListener('scroll', () => {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.syncToScroll(), 120);
      }, {passive: true});

      this.rail.addEventListener('pointerdown', () => this.beginInteraction(), {passive: true});
      this.rail.addEventListener('pointerup', () => this.endInteraction(), {passive: true});
      this.rail.addEventListener('pointercancel', () => this.endInteraction(), {passive: true});
      this.rail.addEventListener('mouseenter', () => this.beginInteraction(), {passive: true});
      this.rail.addEventListener('mouseleave', () => this.endInteraction(), {passive: true});
      this.rail.addEventListener('focusin', () => this.beginInteraction());
      this.rail.addEventListener('focusout', () => this.endInteraction());
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('message', this.onPlayerMessage);
    }

    observeSection() {
      if (!('IntersectionObserver' in window)) {
        this.warmConnections();
        this.isVisible = true;
        this.activateCurrent();
        this.startTimer();
        return;
      }

      this.connectionObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.warmConnections();
        this.connectionObserver.disconnect();
      }, {rootMargin: '700px 0px', threshold: 0});

      this.sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) {
            this.activateCurrent();
            this.startTimer();
          } else {
            this.stopTimer();
            this.pauseAll();
          }
        });
      }, {rootMargin: '100px 0px', threshold: 0.03});

      this.connectionObserver.observe(this.section);
      this.sectionObserver.observe(this.section);
    }

    warmConnections() {
      const candidates = [this.cards[this.index], this.cards[(this.index + 1) % this.cards.length]];
      candidates.forEach((card) => {
        if (!card?.dataset.socialUrl) return;
        const media = this.resolveMedia(card.dataset.socialUrl, card.dataset.platform);
        if (!media?.src) return;

        try {
          addConnectionHint(new URL(media.src, window.location.origin).origin);
        } catch (error) {
          // Invalid URLs are ignored and remain available as ordinary source links.
        }
      });
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopTimer();
        this.pauseAll();
      } else if (this.isVisible) {
        this.activateCurrent();
        this.startTimer();
      }
    }

    beginInteraction() {
      this.isInteracting = true;
      this.stopTimer();
    }

    endInteraction() {
      this.isInteracting = false;
      const card = this.cards[this.index];
      if (card?.dataset.osuPlaybackEnded === 'true') {
        delete card.dataset.osuPlaybackEnded;
        this.goTo(this.index + 1, false);
        return;
      }
      this.startTimer(true);
    }

    startTimer(reset = false) {
      if (!this.autoplay || this.reducedMotion || !this.isVisible || this.isInteracting || document.hidden || this.cards.length < 2) return;
      if (reset) this.stopTimer();
      if (this.timer) return;

      const card = this.cards[this.index];
      if (this.advanceOnEnd && card?.dataset.osuAdvanceMode === 'ended') return;

      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.goTo(this.index + 1, false);
      }, this.interval);
    }

    stopTimer() {
      if (!this.timer) return;
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    goTo(nextIndex, manual) {
      const total = this.cards.length;
      this.index = (nextIndex + total) % total;
      this.setActive(this.index, true);
      this.warmConnections();
      this.startTimer(true);
    }

    setActive(index, shouldScroll) {
      this.cards.forEach((card, cardIndex) => {
        const active = cardIndex === index;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-current', active ? 'true' : 'false');
        if (!active) this.deactivateCard(card);
      });

      if (shouldScroll) this.scrollCardIntoView(this.cards[index]);
      if (this.isVisible) this.activateCurrent();
    }

    scrollCardIntoView(card) {
      if (!card) return;
      const left = card.offsetLeft - ((this.rail.clientWidth - card.offsetWidth) / 2);
      this.rail.scrollTo({left: Math.max(0, left), behavior: this.reducedMotion ? 'auto' : 'smooth'});
    }

    syncToScroll() {
      const railCenter = this.rail.scrollLeft + (this.rail.clientWidth / 2);
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
        const distance = Math.abs(cardCenter - railCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.index) {
        this.index = closestIndex;
        this.setActive(this.index, false);
        this.warmConnections();
        this.startTimer(true);
      }
    }

    activateCurrent() {
      const card = this.cards[this.index];
      if (!card) return;

      const media = this.ensureMedia(card);
      if (media?.tagName === 'VIDEO') this.playVideo(media);
    }

    playVideo(video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = !this.advanceOnEnd;

      if (this.advanceOnEnd && video.dataset.osuEndedBound !== 'true') {
        video.dataset.osuEndedBound = 'true';
        video.addEventListener('ended', () => {
          const card = video.closest('[data-osu-card]');
          if (card) this.advanceFromMedia(card);
        });
        video.addEventListener('error', () => {
          const card = video.closest('[data-osu-card]');
          if (!card || card !== this.cards[this.index]) return;
          card.dataset.osuAdvanceMode = 'fallback';
          this.startTimer(true);
        });
      }

      const play = () => {
        if (!this.isVisible || !video.closest('[data-osu-card]')?.classList.contains('is-active')) return;
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      };

      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        play();
      } else if (!video.dataset.osuWaiting) {
        video.dataset.osuWaiting = 'true';
        video.addEventListener('loadeddata', () => {
          delete video.dataset.osuWaiting;
          requestAnimationFrame(play);
        }, {once: true});
      }
    }

    handlePlayerMessage(event) {
      const card = this.cards[this.index];
      const iframe = card?.querySelector('iframe');
      if (!this.advanceOnEnd || !iframe || event.source !== iframe.contentWindow) return;

      let message = event.data;
      if (typeof message === 'string') {
        try {
          message = JSON.parse(message);
        } catch (error) {
          return;
        }
      }

      if (message?.['x-tiktok-player'] === true && message.type === 'onStateChange' && Number(message.value) === 0) {
        this.advanceFromMedia(card);
      }

      if (message?.['x-tiktok-player'] === true && message.type === 'onPlayerError') {
        card.dataset.osuAdvanceMode = 'fallback';
        this.startTimer(true);
      }
    }

    advanceFromMedia(card) {
      if (!this.autoplay || this.reducedMotion || !this.isVisible || document.hidden || card !== this.cards[this.index]) return;
      if (this.isInteracting) {
        card.dataset.osuPlaybackEnded = 'true';
        return;
      }
      this.goTo(this.index + 1, false);
    }

    pauseAll() {
      this.cards.forEach((card) => this.deactivateCard(card));
    }

    deactivateCard(card) {
      delete card.dataset.osuPlaybackEnded;
      delete card.dataset.osuAdvanceMode;

      const runtimeVideo = card.querySelector('[data-osu-runtime-video]');
      if (runtimeVideo) {
        runtimeVideo.pause();
        runtimeVideo.removeAttribute('src');
        runtimeVideo.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        runtimeVideo.load();
        runtimeVideo.remove();
      }

      const embed = card.querySelector('[data-osu-embed]');
      if (embed && embed.dataset.loaded === 'true') {
        embed.replaceChildren();
        embed.dataset.loaded = 'false';
        embed.classList.remove('has-media');
      }
    }

    ensureMedia(card) {
      const localVideo = card.querySelector('[data-osu-local-video]');
      if (localVideo) {
        let video = localVideo.querySelector('[data-osu-runtime-video]');
        if (video) return video;

        const template = localVideo.querySelector('[data-osu-video-template]');
        video = template?.content.firstElementChild?.cloneNode(true);
        if (!video) return null;

        video.dataset.osuRuntimeVideo = '';
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.loop = !this.advanceOnEnd;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.preload = 'auto';
        localVideo.appendChild(video);
        card.dataset.osuAdvanceMode = 'ended';
        video.load();
        return video;
      }

      const embed = card.querySelector('[data-osu-embed]');
      if (!embed) return null;
      if (embed.dataset.loaded === 'true') return embed.firstElementChild;

      const media = this.resolveMedia(card.dataset.socialUrl, card.dataset.platform);
      if (!media) return null;

      let element;
      if (media.type === 'image') {
        card.dataset.osuAdvanceMode = 'fallback';
        element = document.createElement('img');
        element.src = media.src;
        element.alt = '';
        element.loading = 'eager';
        element.decoding = 'async';
        element.fetchPriority = 'high';
      } else if (media.type === 'video') {
        card.dataset.osuAdvanceMode = 'ended';
        element = document.createElement('video');
        element.src = media.src;
        element.autoplay = false;
        element.loop = !this.advanceOnEnd;
        element.muted = true;
        element.defaultMuted = true;
        element.playsInline = true;
        element.controls = true;
        element.preload = 'auto';
        element.className = 'orlueur-social-ugc__video';
        element.dataset.osuRuntimeVideo = '';
      } else {
        card.dataset.osuAdvanceMode = media.platform === 'tiktok' ? 'ended' : 'fallback';
        element = document.createElement('iframe');
        element.src = media.src;
        element.title = media.title;
        element.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        element.allowFullscreen = true;
        element.loading = 'eager';
        element.referrerPolicy = 'strict-origin-when-cross-origin';

        if (media.platform === 'tiktok') {
          element.addEventListener('load', () => {
            element.contentWindow?.postMessage({type: 'mute', value: null, 'x-tiktok-player': true}, '*');
            element.contentWindow?.postMessage({type: 'play', value: null, 'x-tiktok-player': true}, '*');
          }, {once: true});
        }
      }

      embed.replaceChildren(element);
      embed.dataset.loaded = 'true';
      embed.classList.add('has-media');

      if (element.tagName === 'VIDEO') {
        element.load();
        this.playVideo(element);
      }

      return element;
    }

    resolveMedia(rawUrl, selectedPlatform) {
      if (!rawUrl) return null;

      let url;
      try {
        url = new URL(rawUrl, window.location.origin);
      } catch (error) {
        return null;
      }

      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      const path = url.pathname;
      const platform = selectedPlatform === 'auto' ? this.detectPlatform(host, path) : selectedPlatform;

      if (platform === 'youtube') {
        const id = this.youtubeId(url);
        if (!id) return null;
        return {
          type: 'iframe',
          src: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&mute=1&playsinline=1&loop=${this.advanceOnEnd ? 0 : 1}&playlist=${encodeURIComponent(id)}&controls=1&rel=0`,
          title: 'YouTube community video'
        };
      }

      if (platform === 'tiktok') {
        const match = path.match(/\/video\/(\d+)/);
        if (!match) return null;
        return {
          type: 'iframe',
          src: `https://www.tiktok.com/player/v1/${match[1]}?autoplay=0&loop=${this.advanceOnEnd ? 0 : 1}&music_info=0&description=0&rel=0&closed_caption=0`,
          title: 'TikTok community video',
          platform: 'tiktok'
        };
      }

      if (platform === 'instagram') {
        const match = path.match(/^\/(p|reel|tv)\/([^/]+)/);
        if (!match) return null;
        return {
          type: 'iframe',
          src: `https://www.instagram.com/${match[1]}/${encodeURIComponent(match[2])}/embed/`,
          title: 'Instagram community post'
        };
      }

      const cleanPath = path.toLowerCase();
      if (/\.(mp4|webm|m4v)$/.test(cleanPath)) return {type: 'video', src: url.href};
      if (/\.(avif|gif|jpe?g|png|webp)$/.test(cleanPath)) return {type: 'image', src: url.href};
      return null;
    }

    detectPlatform(host, path) {
      if (host.includes('youtube.com') || host === 'youtu.be') return 'youtube';
      if (host.includes('tiktok.com')) return 'tiktok';
      if (host.includes('instagram.com')) return 'instagram';
      if (/\.(mp4|webm|m4v|avif|gif|jpe?g|png|webp)$/i.test(path)) return 'direct';
      return 'other';
    }

    youtubeId(url) {
      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      if (host === 'youtu.be') return url.pathname.split('/').filter(Boolean)[0] || '';
      if (url.pathname.startsWith('/shorts/')) return url.pathname.split('/')[2] || '';
      if (url.pathname.startsWith('/embed/')) return url.pathname.split('/')[2] || '';
      return url.searchParams.get('v') || '';
    }

    destroy() {
      this.stopTimer();
      this.pauseAll();
      window.clearTimeout(this.scrollTimer);
      this.connectionObserver?.disconnect();
      this.sectionObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('message', this.onPlayerMessage);
    }
  }

  const init = (root = document) => {
    const sections = [];
    if (root instanceof Element && root.matches('[data-orlueur-social-ugc]')) sections.push(root);
    root.querySelectorAll?.('[data-orlueur-social-ugc]').forEach((section) => sections.push(section));

    sections.forEach((section) => {
      if (initialized.has(section)) return;
      initialized.add(section);
      section.osuInstance = new OrlueurSocialUGC(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector?.('[data-orlueur-social-ugc]')?.osuInstance?.destroy();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest?.('[data-orlueur-social-ugc]');
    const card = event.target.closest?.('[data-osu-card]');
    if (!section?.osuInstance || !card) return;
    const index = section.osuInstance.cards.indexOf(card);
    if (index >= 0) section.osuInstance.goTo(index, true);
  });
})();
